import { useState, useEffect } from 'react';
import { Building2, Plus, Search, Users, Calendar, CheckCircle, XCircle, Edit2, Eye } from 'lucide-react';
import { PageHeader } from '../components/common/PageHeader';
import { DataCard } from '../components/common/DataCard';
import { StatsCard } from '../components/common/StatsCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useApp } from '../contexts/AppContext';
import api from '../services/api';
import { toast } from 'sonner';

export function SchoolManagementPage() {
  const { isSuperAdmin } = useApp();
  const [schools, setSchools] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    schoolName: '',
    adminEmail: '',
    adminName: '',
    adminPassword: '',
    planType: 'standard',
    subscriptionMonths: 12,
    schoolLogo: null,      // data-URL string once selected
  });
  const [isCreating, setIsCreating] = useState(false);

  // v96 — School logo upload: read the file into a base64 data URL client-side.
  // Backend re-validates size + MIME.
  const onLogoPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 500 * 1024) {
      toast.error('Logo must be under 500 KB. Please compress and retry.');
      e.target.value = '';
      return;
    }
    const okMimes = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];
    if (!okMimes.includes(f.type)) {
      toast.error('Logo must be PNG, JPEG, WebP or SVG.');
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setCreateForm((p) => ({ ...p, schoolLogo: reader.result }));
    reader.readAsDataURL(f);
  };

  useEffect(() => {
    fetchSchools();
  }, []);

  const fetchSchools = async () => {
    try {
      setIsLoading(true);
      const data = await api.listSchools();
      setSchools(data);
    } catch (error) {
      toast.error('Failed to fetch schools');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateSchool = async (e) => {
    e.preventDefault();
    setIsCreating(true);

    try {
      const newSchool = await api.createSchool(createForm);
      setSchools(prev => [...prev, newSchool]);
      setIsCreateOpen(false);
      setCreateForm({
        schoolName: '',
        adminEmail: '',
        adminName: '',
        adminPassword: '',
        planType: 'standard',
        subscriptionMonths: 12,
        schoolLogo: null,
      });
      toast.success(`School "${newSchool.schoolName}" created successfully!`, {
        description: `School Code: ${newSchool.schoolCode}`,
      });
    } catch (error) {
      toast.error(error.message || 'Failed to create school');
    } finally {
      setIsCreating(false);
    }
  };

  const handleToggleStatus = async (school) => {
    try {
      const newStatus = school.status === 'active' ? 'inactive' : 'active';
      await api.updateSchool(school.schoolId, { status: newStatus });
      setSchools(prev => prev.map(s => 
        s.schoolId === school.schoolId ? { ...s, status: newStatus } : s
      ));
      toast.success(`School ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
    } catch (error) {
      toast.error('Failed to update school status');
    }
  };

  const filteredSchools = schools.filter(school =>
    school.schoolName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    school.schoolCode.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const stats = {
    total: schools.length,
    active: schools.filter(s => s.status === 'active').length,
    inactive: schools.filter(s => s.status === 'inactive').length,
  };

  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-muted-foreground">Access denied. Super Admin only.</p>
      </div>
    );
  }

  return (
    <div data-testid="school-management-page" className="space-y-8 animate-fade-in">
      <PageHeader
        title="School Management"
        description="Create and manage schools on the platform"
      >
        <Button
          onClick={() => setIsCreateOpen(true)}
          className="bg-brand-deep-blue hover:bg-brand-deep-blue/90"
          data-testid="create-school-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create School
        </Button>
      </PageHeader>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <StatsCard title="Total Schools" value={stats.total} icon={Building2} delay={0} />
        <StatsCard title="Active" value={stats.active} delay={0.1} />
        <StatsCard title="Inactive" value={stats.inactive} delay={0.2} />
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search schools..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-9"
          data-testid="school-search"
        />
      </div>

      {/* Schools List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-brand-light-blue border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filteredSchools.length === 0 ? (
        <DataCard className="text-center py-12">
          <Building2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No schools found</p>
        </DataCard>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredSchools.map((school, index) => (
            <DataCard key={school.schoolId} delay={0.1 * index}>
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-xl bg-brand-deep-blue flex items-center justify-center text-white font-bold text-lg">
                    {school.schoolCode.substring(0, 2)}
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-lg text-foreground">
                      {school.schoolName}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Code: <span className="font-mono font-medium">{school.schoolCode}</span>
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="outline">{school.planType}</Badge>
                      <Badge className={school.status === 'active' ? 'bg-emerald-500/10 text-emerald-600' : 'bg-red-500/10 text-red-600'}>
                        {school.status}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-800">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Subscription Start</span>
                    <p className="font-medium">{new Date(school.subscriptionStart).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Subscription End</span>
                    <p className="font-medium">{new Date(school.subscriptionEnd).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-2 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => handleToggleStatus(school)}
                >
                  {school.status === 'active' ? (
                    <><XCircle className="w-4 h-4 mr-1" /> Deactivate</>
                  ) : (
                    <><CheckCircle className="w-4 h-4 mr-1" /> Activate</>
                  )}
                </Button>
                <Button variant="outline" size="sm">
                  <Eye className="w-4 h-4 mr-1" /> View
                </Button>
              </div>
            </DataCard>
          ))}
        </div>
      )}

      {/* Create School Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New School</DialogTitle>
            <DialogDescription>
              Set up a new school on the platform. An admin account will be created automatically.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateSchool} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="schoolName">School Name</Label>
              <Input
                id="schoolName"
                value={createForm.schoolName}
                onChange={(e) => setCreateForm(prev => ({ ...prev, schoolName: e.target.value }))}
                placeholder="e.g., Springfield Academy"
                required
                data-testid="create-school-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="schoolLogo">School Logo (optional · PNG/JPEG/WebP/SVG · max 500 KB)</Label>
              <div className="flex items-center gap-3">
                <input
                  id="schoolLogo"
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  onChange={onLogoPick}
                  className="flex-1 text-sm file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-brand-deep-blue file:text-white hover:file:bg-brand-deep-blue/90"
                  data-testid="create-school-logo-input"
                />
                {createForm.schoolLogo && (
                  <img
                    src={createForm.schoolLogo}
                    alt="Logo preview"
                    className="h-12 w-12 rounded-md object-contain border border-slate-200 bg-white"
                    data-testid="create-school-logo-preview"
                  />
                )}
              </div>
              {createForm.schoolLogo && (
                <button
                  type="button"
                  className="text-xs text-slate-500 hover:text-slate-700 underline"
                  onClick={() => setCreateForm((p) => ({ ...p, schoolLogo: null }))}
                >
                  Remove logo
                </button>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="adminName">Admin Name</Label>
              <Input
                id="adminName"
                value={createForm.adminName}
                onChange={(e) => setCreateForm(prev => ({ ...prev, adminName: e.target.value }))}
                placeholder="e.g., John Smith"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="adminEmail">Admin Email</Label>
              <Input
                id="adminEmail"
                type="email"
                value={createForm.adminEmail}
                onChange={(e) => setCreateForm(prev => ({ ...prev, adminEmail: e.target.value }))}
                placeholder="admin@school.com"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="adminPassword">Admin Password</Label>
              <Input
                id="adminPassword"
                type="password"
                value={createForm.adminPassword}
                onChange={(e) => setCreateForm(prev => ({ ...prev, adminPassword: e.target.value }))}
                placeholder="Secure password"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Plan Type</Label>
                <Select
                  value={createForm.planType}
                  onValueChange={(value) => setCreateForm(prev => ({ ...prev, planType: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basic">Basic</SelectItem>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Subscription (months)</Label>
                <Select
                  value={createForm.subscriptionMonths.toString()}
                  onValueChange={(value) => setCreateForm(prev => ({ ...prev, subscriptionMonths: parseInt(value) }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 month</SelectItem>
                    <SelectItem value="6">6 months</SelectItem>
                    <SelectItem value="12">12 months</SelectItem>
                    <SelectItem value="24">24 months</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isCreating} className="bg-brand-deep-blue">
                {isCreating ? 'Creating...' : 'Create School'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
